import { callMutation } from '@darice/graphql-client';
import { parseAuthSessionResponse, parseSwitchTenantResponse } from './dto';
import { globalSignOut, refreshEmailOtpSession, revokeEmailOtpRefreshToken } from './cognitoOtp';
import { clearSessionCookie, decodeJwtPayload, parseSessionCookieJwt, readSessionCookie, writeSessionCookie, } from './sessionCookie';
const REFRESH_KEY = 'darice_refresh_token';
const TRUST_KEY = 'darice_trusted_device';
const SIGNED_IN_KEY = 'darice_signed_in_at';
const ACCESS_KEY = 'darice_access_token';
const TRIAL_KEY = 'darice_auth_trial';
const EMAIL_KEY = 'darice_auth_email';
const HINT_KEY = 'darice_passkey_hint';
const TRUST_MS = 30 * 24 * 60 * 60 * 1000;
export const AUTH_TRIAL_GROUP = 'darice-auth-trial';
let access = null;
let refreshing = null;
let sessionGeneration = 0;
const REFRESH_LEAD_MS = 5 * 60 * 1000;
const MAX_TIMEOUT_MS = 2_147_483_647;
let current = null;
let refreshTimer = null;
const COMPLETE_COGNITO_MUTATION = `mutation CompleteCognito($idToken:String!){
  completeCognitoSession(idToken:$idToken){
    success sessionJwt refreshToken expiresAt activeTenantId
    tenantMemberships{ tenantId role } error
  }
}`;
const SWITCH_MUTATION = `mutation Switch($t:ID!){
  switchTenant(newTenantId:$t){ success sessionJwt expiresAt error }
}`;
function clearRefreshTimer() {
    if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
}
function scheduleRefresh(expiresAt) {
    clearRefreshTimer();
    const delay = Math.min(MAX_TIMEOUT_MS, Math.max(0, expiresAt * 1000 - Date.now() - REFRESH_LEAD_MS));
    refreshTimer = setTimeout(() => {
        refreshSession().catch(() => { });
    }, delay);
}
// When no in-page session exists, adopt the darice-session domain cookie the
// auth portal set (design §3.14 step 7) so destination apps boot authenticated.
// Hydration is read-only: it schedules no refresh timer, because only the
// portal origin holds the Cognito refresh token.
export function getSession() {
    if (current && current.expiresAt * 1000 > Date.now())
        return current;
    current = null;
    const cookieJwt = readSessionCookie();
    if (cookieJwt === null)
        return null;
    const payload = parseSessionCookieJwt(cookieJwt, Date.now() / 1000);
    if (!payload)
        return null;
    current = { jwt: cookieJwt, expiresAt: payload.expiresAt };
    return current;
}
export function setSession(jwt, expiresAt) {
    current = { jwt, expiresAt };
    writeSessionCookie(jwt, expiresAt);
    scheduleRefresh(expiresAt);
}
export function clearSession() {
    current = null;
    clearSessionCookie();
    clearRefreshTimer();
}
export function getActiveTenantId() {
    if (!current)
        return null;
    const p = decodeJwtPayload(current.jwt);
    return p?.activeTenantId ?? null;
}
export function getTenantMemberships() {
    if (!current)
        return [];
    const p = decodeJwtPayload(current.jwt);
    return p?.tenantMemberships ?? [];
}
function applyAuthSessionResponse(res) {
    if (!res.success || !res.sessionJwt || res.expiresAt == null) {
        throw new Error(res.error ?? 'AUTH_FAILED');
    }
    setSession(res.sessionJwt, res.expiresAt);
}
export function isAuthTrialMember() {
    return sessionStorage.getItem(TRIAL_KEY) === 'true';
}
export function getSignedInEmail() { return sessionStorage.getItem(EMAIL_KEY); }
export function getPasskeyHint() {
    try {
        const hint = JSON.parse(localStorage.getItem(HINT_KEY) ?? 'null');
        return typeof hint?.email === 'string' && hint.email.includes('@') ? { email: hint.email } : null;
    }
    catch {
        return null;
    }
}
export function setPasskeyHint(email) {
    localStorage.setItem(HINT_KEY, JSON.stringify({ email: email.trim().toLowerCase() }));
}
export function removePasskeyHint() { localStorage.removeItem(HINT_KEY); }
function purgeTrust() {
    localStorage.removeItem(TRUST_KEY);
    localStorage.removeItem(REFRESH_KEY);
}
export function getTrustedUntil() {
    try {
        const value = JSON.parse(localStorage.getItem(TRUST_KEY) ?? 'null');
        if (typeof value?.trustedUntil === 'number' && Number.isFinite(value.trustedUntil)
            && value.trustedUntil > Date.now() && value.trustedUntil <= Date.now() + TRUST_MS
            && localStorage.getItem(REFRESH_KEY))
            return value.trustedUntil;
    }
    catch { /* Malformed or expired trust is removed. */ }
    purgeTrust();
    return null;
}
export function isDeviceTrusted() { return getTrustedUntil() !== null; }
// The portal needs a refresh session; a surviving app cookie alone cannot
// establish an untrusted session after the browser process has restarted.
function refreshStorage() { return isDeviceTrusted() ? localStorage : sessionStorage; }
export function trustThisDevice() {
    const token = refreshStorage().getItem(REFRESH_KEY);
    const signedInAt = Number(sessionStorage.getItem(SIGNED_IN_KEY));
    if (!token || !signedInAt || signedInAt > Date.now() || signedInAt + TRUST_MS <= Date.now()) {
        throw new Error('Please sign in again before trusting this device.');
    }
    localStorage.setItem(REFRESH_KEY, token);
    localStorage.setItem(TRUST_KEY, JSON.stringify({ trustedUntil: signedInAt + TRUST_MS }));
    sessionStorage.removeItem(REFRESH_KEY);
}
export function forgetThisDevice() {
    const trustedUntil = getTrustedUntil();
    if (trustedUntil !== null) {
        sessionStorage.setItem(SIGNED_IN_KEY, String(trustedUntil - TRUST_MS));
        const token = localStorage.getItem(REFRESH_KEY);
        if (token)
            sessionStorage.setItem(REFRESH_KEY, token);
    }
    purgeTrust();
}
function storeClaims(idToken, accessToken) {
    const payload = decodeJwtPayload(idToken);
    const groups = payload?.['cognito:groups'];
    sessionStorage.setItem(TRIAL_KEY, String(Array.isArray(groups) && groups.includes(AUTH_TRIAL_GROUP)));
    if (typeof payload?.email === 'string')
        sessionStorage.setItem(EMAIL_KEY, payload.email);
    else
        sessionStorage.removeItem(EMAIL_KEY);
    access = null;
    sessionStorage.removeItem(ACCESS_KEY);
    if (accessToken) {
        const exp = decodeJwtPayload(accessToken)?.exp;
        if (typeof exp !== 'number' || !Number.isFinite(exp))
            throw new Error('Invalid access token expiry');
        access = { token: accessToken, expiresAt: exp };
        sessionStorage.setItem(ACCESS_KEY, JSON.stringify(access));
    }
}
async function completeSession(idToken, accessToken, generation = sessionGeneration) {
    const res = parseAuthSessionResponse(await callMutation(COMPLETE_COGNITO_MUTATION, { idToken }, { useApiKey: true }));
    if (generation !== sessionGeneration)
        throw new Error('Session changed during refresh');
    applyAuthSessionResponse(res);
    storeClaims(idToken, accessToken);
    return res;
}
export async function completeCognitoSession(idToken, refreshToken, accessToken) {
    const generation = ++sessionGeneration;
    const res = await completeSession(idToken, accessToken, generation);
    // A fresh interactive sign-in starts a new token family and requires a new opt-in.
    purgeTrust();
    sessionStorage.removeItem(REFRESH_KEY);
    const token = refreshToken ?? res.refreshToken;
    if (token)
        sessionStorage.setItem(REFRESH_KEY, token);
    sessionStorage.setItem(SIGNED_IN_KEY, String(Date.now()));
}
export function refreshSession() {
    // Timers, focus and access-token requests must not race token rotation in this tab.
    refreshing ??= performRefresh().finally(() => { refreshing = null; });
    return refreshing;
}
async function performRefresh() {
    const generation = sessionGeneration;
    const storage = refreshStorage();
    const refreshToken = storage.getItem(REFRESH_KEY);
    if (!refreshToken) {
        clearSession();
        throw new Error('FAIL: missing Cognito refresh token');
    }
    const tokens = await refreshEmailOtpSession(refreshToken);
    // Save a rotated token before the session mutation, so a retry can use it.
    if (generation !== sessionGeneration || storage.getItem(REFRESH_KEY) !== refreshToken)
        throw new Error('Session changed during refresh');
    if (storage === localStorage && !isDeviceTrusted())
        throw new Error('Trusted device expired');
    storage.setItem(REFRESH_KEY, tokens.refreshToken);
    await completeSession(tokens.idToken, tokens.accessToken, generation);
}
export async function resumeTrustedSession() {
    if (!isDeviceTrusted())
        return false;
    try {
        await refreshSession();
        return true;
    }
    catch {
        purgeLocalSession();
        return false;
    }
}
export async function getAccessToken() {
    if (!access) {
        try {
            const saved = JSON.parse(sessionStorage.getItem(ACCESS_KEY) ?? 'null');
            if (typeof saved?.token === 'string' && typeof saved.expiresAt === 'number')
                access = saved;
        }
        catch {
            sessionStorage.removeItem(ACCESS_KEY);
        }
    }
    if (!access || access.expiresAt * 1000 <= Date.now())
        await refreshSession();
    if (!access || access.expiresAt * 1000 <= Date.now())
        throw new Error('Please sign in again.');
    return access.token;
}
function purgeLocalSession() {
    sessionGeneration++;
    clearSession();
    purgeTrust();
    access = null;
    for (const key of [REFRESH_KEY, ACCESS_KEY, SIGNED_IN_KEY, TRIAL_KEY, EMAIL_KEY])
        sessionStorage.removeItem(key);
}
export async function switchTenant(tenantId) {
    return parseSwitchTenantResponse(await callMutation(SWITCH_MUTATION, { t: tenantId }));
}
// Logout per design §3.14: clear the domain session cookie plus in-page state,
// then kill the Cognito session server-side by revoking the refresh token
// (which revokes its descendant tokens). Local state is cleared before the
// network call so a revocation failure still leaves this browser signed out.
export async function signOut() {
    const refreshToken = refreshStorage().getItem(REFRESH_KEY);
    purgeLocalSession();
    if (refreshToken) {
        await revokeEmailOtpRefreshToken(refreshToken);
    }
    window.location.href = '/';
}
export async function signOutEverywhere() {
    const token = await getAccessToken();
    await globalSignOut(token);
    purgeLocalSession();
    window.location.href = '/';
}
//# sourceMappingURL=auth.js.map